import React, { useRef, useState } from 'react';
import {
  IonPage,
  IonContent,
  IonItem,
  IonLabel,
  IonInput,
  IonText,
  IonIcon,
  useIonViewWillLeave,
} from '@ionic/react';
import { callOutline, mailOutline } from 'ionicons/icons';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import { useUserContext } from '@/modules/user/useUserContext';
import { useProfileWizardContext } from '@/contexts/ProfileWizardContext';
import { useAppToast } from '@/hooks/useAppToast';
import PhoneInput from '@/components/PhoneInput';
import EmailVerificationModal from '@/modules/idv/EmailVerificationModal';
import { PhoneLogic } from '@/modules/phone';
import { MePrFoService } from '@/services/meprfo';
import { apiCall } from '@/util/api';
import { PHONE_MIGRATION_PROOF_WINDOW_MS, pxToRem } from '@/modules/config';
import { ProfileWizardLogic } from './logic';
import ProfileWizardHeader from './ProfileWizardHeader';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * PhoneMigrationEmailStep (#441): the first half of the email proof method —
 * prove control of the inbox ON FILE for the old number. The caller
 * enters the old phone AND its associated email; we only open the email
 * verification modal once the entered email actually matches the address on
 * file for that number.
 *
 * Association check: obliviously hash the old number → look up its profile →
 * identify to it (the userId derived from a phone is this app's capability, the
 * same exchange PhoneStep does) → read the on-file email and compare. The
 * comparison only GATES the modal; the backend independently rejects a
 * mismatched address (a re-verify of anything but the on-file address is
 * treated as a change and refused), so a bypassed client check earns nothing.
 * The confirm earns an email claim, not a migration proof: inbox access alone
 * must not move the account. The questions step follows and, with that claim
 * live, mints the proof; the new-phone step then finishes the switch.
 */
const PhoneMigrationEmailStep: React.FC = () => {
  const { setPendingEmail: setContextPendingEmail, refreshUserContext } =
    useUserContext();
  const {
    setOriginalPhone,
    urlProvidedOrigin,
    routerProvidedOrigin,
    navigateWithOrigin,
    navigateToStep,
    progress,
  } = useProfileWizardContext();
  const [presentToast] = useAppToast();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  const verificationSucceededRef = useRef(false);
  const [oldPhone, setOldPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const oldPhoneObj = PhoneLogic.tryCreate(oldPhone);
  const emailIsValid = EMAIL_REGEX.test(email.trim());
  const canSubmit = oldPhoneObj !== null && emailIsValid && !isChecking;

  const proofWindowMinutes = Math.round(
    PHONE_MIGRATION_PROOF_WINDOW_MS / 60000
  );

  const handleVerifyEmail = async () => {
    if (!oldPhoneObj) {
      presentToast({
        message: 'Enter a valid US phone number.',
        color: 'danger',
        duration: 3000,
      });
      return;
    }
    if (!emailIsValid) {
      presentToast({
        message: 'Enter a valid email address.',
        color: 'danger',
        duration: 3000,
      });
      return;
    }

    setIsChecking(true);
    try {
      // Find the profile that owns the old number.
      const { userId: existingUserId } = await MePrFoService.checkPhoneExists(
        oldPhoneObj.value
      );
      if (!existingUserId) {
        presentToast({
          message: "We couldn't find a Snowball profile for that number.",
          color: 'danger',
          duration: 4000,
        });
        return;
      }

      // Exchange the session for that profile so we can read its on-file email.
      const identifyResponse = await apiCall('/api/auth/identify', {
        method: 'POST',
        body: JSON.stringify({ id: existingUserId }),
      });
      if (!identifyResponse.ok) {
        throw new Error('Failed to look up that profile. Please try again.');
      }

      const user = await refreshUserContext();
      const onFileEmail = user?.email;
      if (!onFileEmail) {
        presentToast({
          message:
            'That number has no email address on file — try another method.',
          color: 'danger',
          duration: 4000,
        });
        return;
      }

      if (email.trim().toLowerCase() !== onFileEmail.toLowerCase()) {
        presentToast({
          message: "That email isn't the one associated with this number.",
          color: 'danger',
          duration: 4000,
        });
        return;
      }

      // Match: stage the old number (for the migration change notice) and the
      // canonical on-file address, then open the verification modal.
      setOriginalPhone(oldPhone);
      setContextPendingEmail(onFileEmail);
      setShowModal(true);
    } catch (error) {
      presentToast({
        message: error instanceof Error ? error.message : 'An error occurred',
        color: 'danger',
        duration: 3000,
      });
    } finally {
      setIsChecking(false);
    }
  };

  // A claimless re-verify earns only a short-window email claim, so the
  // questions have to be answered within that window.
  const announceWindowAndAdvance = () => {
    navigateWithOrigin('/profile/steps/phone-migration-questions', 'forward');
    presentToast({
      message: `Email verified. You have ${proofWindowMinutes} minutes to answer the questions`,
      color: 'success',
      duration: 6000,
      htmlAttributes: { 'data-testid': 'phone-migration-email-verified-toast' },
    });
  };

  const handleModalClose = (reason?: string) => {
    setShowModal(false);

    if (reason === 'success') {
      verificationSucceededRef.current = true;
      return;
    }
    if (verificationSucceededRef.current) {
      verificationSucceededRef.current = false;
      return;
    }

    if (reason === 'expired') {
      presentToast({
        message: 'Verification code expired. Please try verifying again.',
        color: 'warning',
        duration: 5000,
      });
    }
  };

  const handleModalSuccess = () => {
    verificationSucceededRef.current = true;
    setShowModal(false);
    announceWindowAndAdvance();
  };

  const getContinuePath = (): string => {
    const origin = routerProvidedOrigin || urlProvidedOrigin || '/';
    return `/profile/steps/phone-migration-questions?origin=${encodeURIComponent(origin)}`;
  };

  return (
    <IonPage>
      <ProfileWizardHeader
        title={ProfileWizardLogic.titleForStep('phone-migration-email')}
        progress={progress}
        onBack={() =>
          navigateToStep(
            ProfileWizardLogic.previousStep('phone-migration-email'),
            'back'
          )
        }
      />
      <IonContent>
        <div style={{ padding: '20px 20px 120px' }}>
          <IonText>
            <p style={{ fontSize: pxToRem(16), marginBottom: '10px' }}>
              Enter your old number and the email address on file for it. If
              they match, we'll send a verification email.
            </p>
          </IonText>
          <IonItem>
            <IonLabel position="stacked">
              <IonIcon icon={callOutline} style={{ marginRight: '5px' }} />
              Your old phone number
            </IonLabel>
            <PhoneInput
              value={oldPhone}
              onPhoneChange={setOldPhone}
              placeholder="(555) 123-4567"
              showValidation={true}
            />
          </IonItem>
          <IonItem>
            <IonLabel position="stacked">
              <IonIcon icon={mailOutline} style={{ marginRight: '5px' }} />
              Email address for that number
            </IonLabel>
            <IonInput
              type="email"
              value={email}
              onIonInput={e => setEmail(e.detail.value ?? '')}
              placeholder="your.email@example.com"
              autocomplete="off"
              autocapitalize="none"
              data-testid="pm-email-input"
            />
          </IonItem>
        </div>
      </IonContent>

      <FooterDock background="transparent">
        <FooterDockRow landmark>
          <SmartCaseButton
            expand="block"
            onClick={handleVerifyEmail}
            disabled={!canSubmit}
            color="primary"
            data-testid="profile-wizard-next"
            style={{ margin: 0 }}
          >
            {isChecking ? 'Checking…' : 'Verify email'}
          </SmartCaseButton>
        </FooterDockRow>
      </FooterDock>

      <EmailVerificationModal
        isOpen={showModal}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        continuePath={getContinuePath()}
      />
    </IonPage>
  );
};

export default PhoneMigrationEmailStep;
